/**
 * Webtech HR Engine™ - Development Plans Management
 * Gestion des plans de développement des compétences
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ArrowLeft,
  Plus,
  Search,
  Target,
  MoreVertical,
  CheckCircle2,
  Clock,
  Trash2,
  Loader2,
  User,
  Award,
  Calendar,
  BookOpen,
  GraduationCap,
  Briefcase,
  Play
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';

const ACTION_TYPES = [
  { value: 'formation', label: 'Formation', icon: GraduationCap },
  { value: 'coaching', label: 'Coaching', icon: User },
  { value: 'projet', label: 'Projet pratique', icon: Briefcase },
  { value: 'autoformation', label: 'Autoformation', icon: BookOpen },
];

const SKILL_LEVELS = {
  1: 'Débutant',
  2: 'Intermédiaire',
  3: 'Confirmé',
  4: 'Avancé',
  5: 'Expert',
};

export default function DevelopmentPlansPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyId = user?.companyId;
  const preselectedEmployeeId = searchParams.get('employee');
  
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [skills, setSkills] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, plan: null });
  const [completeDialog, setCompleteDialog] = useState({ open: false, plan: null, finalLevel: 3 });
  
  // Form state
  const [formData, setFormData] = useState({
    employeeId: preselectedEmployeeId || '',
    skillId: '',
    currentLevel: 2,
    targetLevel: 4,
    deadline: '',
    notes: '',
    actions: []
  });
  
  // New action form
  const [newAction, setNewAction] = useState({
    type: 'formation',
    title: '',
    description: '',
    deadline: ''
  });

  useEffect(() => {
    if (companyId) loadData();
  }, [companyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Use the correct upskilling-plans endpoint for Admin RH (get ALL plans)
      const [plansRes, empRes, skillsRes] = await Promise.all([
        api.get(`/skills-intelligence/upskilling-plans?company_id=${companyId}`),
        api.get('/employees?limit=10000'),
        api.get(`/skills/library?company_id=${companyId}`)
      ]);
      
      const employeesList = empRes.data.employees || empRes.data || [];
      const skillsList = skillsRes.data.skills || [];
      const rawPlans = plansRes.data.plans || plansRes.data || [];
      
      // Enrich plans with employee and skill data
      const enrichedPlans = rawPlans.map(plan => {
        const employee = employeesList.find(e => e.id === plan.employeeId);
        const skill = skillsList.find(s => s.id === plan.skillId);
        return {
          ...plan,
          employee: employee || { firstName: 'Inconnu', lastName: '' },
          skill: skill || { name: plan.skillName || 'Compétence' }
        };
      });
      
      setPlans(enrichedPlans);
      setEmployees(employeesList);
      setSkills(skillsList);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const addAction = () => {
    if (!newAction.title.trim()) {
      toast.error('Le titre de l\'action est requis');
      return;
    }
    setFormData(prev => ({
      ...prev,
      actions: [...prev.actions, { ...newAction, status: 'planned' }]
    }));
    setNewAction({ type: 'formation', title: '', description: '', deadline: '' });
  };

  const removeAction = (index) => {
    setFormData(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }));
  };

  const handleCreate = async () => {
    if (!formData.employeeId || !formData.skillId) {
      toast.error('Veuillez sélectionner un employé et une compétence');
      return;
    }
    
    // Get skill name for the plan
    const selectedSkill = skills.find(s => s.id === formData.skillId);
    
    try {
      await api.post(`/skills-intelligence/upskilling-plans?company_id=${companyId}&created_by=${user.id}`, {
        ...formData,
        skillName: selectedSkill?.name || ''
      });
      toast.success('Plan de développement créé avec succès !');
      setCreateDialog(false);
      setFormData({
        employeeId: '',
        skillId: '',
        currentLevel: 2,
        targetLevel: 4,
        deadline: '',
        notes: '',
        actions: []
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleComplete = async () => {
    try {
      // Complete the plan by updating all actions
      await api.put(
        `/skills-intelligence/upskilling-plans/${completeDialog.plan.id}?company_id=${companyId}`,
        { status: 'completed', progress: 100 }
      );
      toast.success('Plan complété avec succès');
      setCompleteDialog({ open: false, plan: null, finalLevel: 3 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/skills-intelligence/upskilling-plans/${deleteDialog.plan.id}?company_id=${companyId}`);
      toast.success('Plan supprimé');
      setDeleteDialog({ open: false, plan: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const filteredPlans = plans.filter(p => {
    const matchesSearch = 
      p.employee?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.employee?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.skill?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const isAdmin = user?.role === 'SuperAdmin' || user?.role === 'Admin';
  const canCreatePlan = isAdmin || user?.role === 'Manager';

  return (
    <DashboardLayout title="Plans de développement">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate('/skills')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Target className="h-7 w-7 text-webtech-blue" />
              Plans de développement
            </h1>
            <p className="text-slate-500 mt-1">Suivez la progression des compétences</p>
          </div>
          
          {canCreatePlan && (
            <Button onClick={() => setCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau plan
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-l-4 border-l-slate-500">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900">{plans.length}</div>
                <p className="text-sm text-slate-500 mt-1">Total des plans</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {plans.filter(p => p.status === 'active').length}
                </div>
                <p className="text-sm text-slate-500 mt-1">En cours</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {plans.filter(p => p.status === 'completed').length}
                </div>
                <p className="text-sm text-slate-500 mt-1">Complétés</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">
                  {plans.length > 0 ? Math.round(plans.reduce((sum, p) => sum + (p.progress || 0), 0) / plans.length) : 0}%
                </div>
                <p className="text-sm text-slate-500 mt-1">Progression moy.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">
                  {new Set(plans.map(p => p.employeeId)).size}
                </div>
                <p className="text-sm text-slate-500 mt-1">Collaborateurs</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="active">Actif</SelectItem>
              <SelectItem value="completed">Complété</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Plans List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-webtech-blue" />
          </div>
        ) : plans.length === 0 ? (
          <Card className="py-12">
            <CardContent className="text-center">
              <Target className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun plan de développement</h3>
              <p className="text-slate-500 mb-6">Créez des plans pour suivre la progression.</p>
              {canCreatePlan && (
                <Button onClick={() => setCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Créer un plan
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredPlans.map(plan => (
              <Card key={plan.id} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-webtech-blue/10 flex items-center justify-center">
                        <User className="h-6 w-6 text-webtech-blue" />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">
                          {plan.employee?.firstName} {plan.employee?.lastName}
                        </h4>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Award className="h-4 w-4" />
                          <span>{plan.skill?.name || 'Compétence'}</span>
                          <span>•</span>
                          <span>Niveau {plan.currentLevel} → {plan.targetLevel}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right hidden md:block">
                        <p className="text-lg font-bold text-slate-900">{plan.progress || 0}%</p>
                        <p className="text-xs text-slate-500">Progression</p>
                      </div>
                      <Progress value={plan.progress || 0} className="w-32 h-2 hidden md:block" />
                      
                      <Badge className={plan.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                        {plan.status === 'active' ? (
                          <><Clock className="h-3 w-3 mr-1" /> Actif</>
                        ) : (
                          <><CheckCircle2 className="h-3 w-3 mr-1" /> Complété</>
                        )}
                      </Badge>
                      
                      {isAdmin && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {plan.status === 'active' && (
                              <DropdownMenuItem onClick={() => setCompleteDialog({ open: true, plan, finalLevel: plan.targetLevel })}>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Marquer complété
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => setDeleteDialog({ open: true, plan })}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  {plan.actions && plan.actions.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-medium text-slate-700 mb-2">Actions ({plan.actions.length})</p>
                      <div className="flex gap-2 flex-wrap">
                        {plan.actions.slice(0, 3).map((action, idx) => {
                          const ActionType = ACTION_TYPES.find(t => t.value === action.type);
                          const ActionIcon = ActionType?.icon || BookOpen;
                          return (
                            <Badge key={idx} variant="outline" className="gap-1">
                              <ActionIcon className="h-3 w-3" />
                              {action.title}
                            </Badge>
                          );
                        })}
                        {plan.actions.length > 3 && (
                          <Badge variant="outline">+{plan.actions.length - 3}</Badge>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouveau plan de développement</DialogTitle>
            <DialogDescription>
              Créez un plan pour développer une compétence spécifique.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Collaborateur *</Label>
                <Select 
                  value={formData.employeeId} 
                  onValueChange={(v) => setFormData({ ...formData, employeeId: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.filter(e => e.status === 'Actif').map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{emp.firstName} {emp.lastName}</span>
                          {emp.jobTitle && <span className="text-xs text-slate-400">• {emp.jobTitle}</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Compétence *</Label>
                <Select 
                  value={formData.skillId} 
                  onValueChange={(v) => setFormData({ ...formData, skillId: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {skills.map(skill => (
                      <SelectItem key={skill.id} value={skill.id}>
                        {skill.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Niveau actuel: {formData.currentLevel} - {SKILL_LEVELS[formData.currentLevel]}</Label>
                <Slider
                  value={[formData.currentLevel]}
                  onValueChange={([v]) => setFormData({ ...formData, currentLevel: v })}
                  min={1}
                  max={5}
                  step={1}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Niveau cible: {formData.targetLevel} - {SKILL_LEVELS[formData.targetLevel]}</Label>
                <Slider
                  value={[formData.targetLevel]}
                  onValueChange={([v]) => setFormData({ ...formData, targetLevel: v })}
                  min={1}
                  max={5}
                  step={1}
                  className="mt-2"
                />
              </div>
            </div>
            
            <div>
              <Label>Date limite</Label>
              <Input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notes sur le plan..."
                rows={2}
              />
            </div>
            
            {/* Actions */}
            <div>
              <Label className="text-base font-semibold">Actions de développement</Label>
              
              {formData.actions.length > 0 && (
                <div className="space-y-2 mt-2 mb-4">
                  {formData.actions.map((action, idx) => {
                    const ActionType = ACTION_TYPES.find(t => t.value === action.type);
                    return (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{ActionType?.label}</Badge>
                          <span className="text-sm">{action.title}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeAction(idx)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              
              <div className="border rounded-lg p-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Select value={newAction.type} onValueChange={(v) => setNewAction({ ...newAction, type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTION_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Titre de l'action"
                    value={newAction.title}
                    onChange={(e) => setNewAction({ ...newAction, title: e.target.value })}
                  />
                </div>
                <Input
                  placeholder="Description (optionnel)"
                  value={newAction.description}
                  onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
                />
                <Button variant="outline" size="sm" onClick={addAction}>
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter l'action
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate}>
              Créer le plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={completeDialog.open} onOpenChange={(open) => setCompleteDialog({ ...completeDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Compléter le plan</DialogTitle>
            <DialogDescription>
              Indiquez le niveau final atteint pour cette compétence.
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label>Niveau final atteint: {completeDialog.finalLevel} - {SKILL_LEVELS[completeDialog.finalLevel]}</Label>
            <Slider
              value={[completeDialog.finalLevel]}
              onValueChange={([v]) => setCompleteDialog({ ...completeDialog, finalLevel: v })}
              min={1}
              max={5}
              step={1}
              className="mt-2"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteDialog({ ...completeDialog, open: false })}>
              Annuler
            </Button>
            <Button onClick={handleComplete}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Valider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Supprimer le plan</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce plan de développement ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
